import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Card,
  Result,
  Button,
  Descriptions,
  Space,
  Divider,
  Tag,
  Spin,
  message,
  Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import orderService from '../../services/orderService';
import './OrderConfirmationPage.scss';

const { Title, Text } = Typography;

const OrderConfirmationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [order, setOrder] = useState(location.state?.order || null);
  const [orders, setOrders] = useState([]); // For unified checkout - multiple orders
  const [isUnified, setIsUnified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'cancelled', 'pending'
  const [payingNow, setPayingNow] = useState(false);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const sessionId = searchParams.get('session_id');
      const orderId = searchParams.get('order_id');
      const checkoutGroupId = searchParams.get('checkout_group_id');
      const paymentSuccess = searchParams.get('payment_success');
      const paymentCancelled = searchParams.get('payment_cancelled');
      const unified = searchParams.get('unified');

      // UNIFIED CHECKOUT - returning from Stripe with success
      if (unified && paymentSuccess && sessionId && checkoutGroupId) {
        setVerifying(true);
        setIsUnified(true);
        const result = await orderService.verifyUnifiedPayment(sessionId, checkoutGroupId);

        if (result.success && result.data.success) {
          setOrders(result.data.orders);
          setOrder(result.data.orders[0]); // Set first order for display compatibility
          setPaymentStatus('success');
          message.success(t('orderConfirmation.paymentSuccess'));
        } else {
          setPaymentStatus('pending');
          message.error(result.data?.message || t('orderConfirmation.paymentVerificationFailed'));
        }
        setVerifying(false);
        setSearchParams({});
      }
      // SINGLE ORDER - returning from Stripe with success
      else if (paymentSuccess && sessionId && orderId) {
        setVerifying(true);
        const result = await orderService.verifyPayment(sessionId, orderId);

        if (result.success && result.data.success) {
          setOrder(result.data.order);
          setPaymentStatus('success');
          message.success(t('orderConfirmation.paymentSuccess'));
        } else {
          setPaymentStatus('pending');
          message.error(result.data?.message || t('orderConfirmation.paymentVerificationFailed'));
          // Fetch order details anyway
          const orderResult = await orderService.getOrderById(orderId);
          if (orderResult.success) {
            setOrder(orderResult.data);
          }
        }
        setVerifying(false);
        // Clear URL params
        setSearchParams({});
      }
      // UNIFIED CHECKOUT - payment cancelled
      else if (unified && paymentCancelled && checkoutGroupId) {
        setPaymentStatus('cancelled');
        setIsUnified(true);
        message.info(t('orderConfirmation.paymentCancelled'));
        // Navigate to orders page since we don't have easy way to repay unified
        navigate('/orders');
      }
      // SINGLE ORDER - payment cancelled
      else if (paymentCancelled && orderId) {
        setPaymentStatus('cancelled');
        message.info(t('orderConfirmation.paymentCancelled'));
        // Fetch order details
        setLoading(true);
        const orderResult = await orderService.getOrderById(orderId);
        if (orderResult.success) {
          setOrder(orderResult.data);
        }
        setLoading(false);
        // Clear URL params
        setSearchParams({});
      }
      // No order from state and no URL params - redirect
      else if (!order) {
        navigate('/orders');
      }
    };

    checkPaymentStatus();
  }, []);

  const handlePayNow = async () => {
    if (!order) return;

    setPayingNow(true);
    const result = await orderService.createPaymentSession(order.id);

    if (result.success) {
      window.location.href = result.data.checkout_url;
    } else {
      message.error(result.error || t('orderConfirmation.paymentSessionFailed'));
      setPayingNow(false);
    }
  };

  if (loading || verifying) {
    return (
      <div className="order-confirmation-page">
        <div className="sweet-container" style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 24 }}>
            {verifying ? t('orderConfirmation.verifyingPayment') : t('common.loading')}
          </Title>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      PROCESSING: 'cyan',
      SHIPPED: 'purple',
      DELIVERED: 'green',
      CANCELLED: 'red',
    };
    return colors[status] || 'default';
  };

  const isPending = order.status === 'PENDING';
  const isConfirmed = order.status === 'CONFIRMED';

  // Determine result status and content
  let resultStatus = 'success';
  let resultIcon = <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  let resultTitle = t('orderConfirmation.successTitle');
  let resultSubtitle = t('orderConfirmation.successSubtitle');

  if (isPending) {
    resultStatus = 'warning';
    resultIcon = <ClockCircleOutlined style={{ color: '#faad14' }} />;
    resultTitle = t('orderConfirmation.pendingTitle');
    resultSubtitle = t('orderConfirmation.pendingSubtitle');
  } else if (paymentStatus === 'cancelled') {
    resultStatus = 'warning';
    resultIcon = <CloseCircleOutlined style={{ color: '#faad14' }} />;
    resultTitle = t('orderConfirmation.cancelledTitle');
    resultSubtitle = t('orderConfirmation.cancelledSubtitle');
  }

  // Calculate unified total if multiple orders
  const unifiedTotal = isUnified && orders.length > 0
    ? orders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0)
    : 0;

  return (
    <div className="order-confirmation-page">
      <div className="sweet-container">
        <Result
          status={resultStatus}
          icon={resultIcon}
          title={resultTitle}
          subTitle={
            <Space direction="vertical" size="small">
              <Text>{resultSubtitle}</Text>
              {isUnified && orders.length > 0 ? (
                <Text strong>
                  {t('orderConfirmation.ordersCreated', { count: orders.length })}
                </Text>
              ) : (
                <Text strong>{t('orderConfirmation.orderNumber')}: {order.order_number}</Text>
              )}
            </Space>
          }
        />

        {/* Payment Required Alert */}
        {isPending && (
          <Alert
            message={t('orderConfirmation.paymentRequired')}
            description={t('orderConfirmation.paymentRequiredDesc')}
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
            action={
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                onClick={handlePayNow}
                loading={payingNow}
              >
                {t('orderConfirmation.payNow')}
              </Button>
            }
          />
        )}

        {/* Payment Success Alert */}
        {paymentStatus === 'success' && (
          <Alert
            message={t('orderConfirmation.paymentSuccessAlert')}
            description={t('orderConfirmation.paymentSuccessDesc')}
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {isUnified && orders.length > 0 ? (
          /* UNIFIED CHECKOUT - Show all orders */
          <>
            {orders.map((ord, index) => (
              <Card key={ord.id} className="order-details-card" style={{ marginBottom: 16 }}>
                <Title level={4}>{ord.partner_name}</Title>
                <Text type="secondary">{t('orderConfirmation.orderNumber')}: {ord.order_number}</Text>
                <Divider />

                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {ord.items && ord.items.map((item) => (
                    <div key={item.id} className="order-item">
                      <div>
                        <Text>{item.product_name}</Text>
                        <br />
                        <Text type="secondary">
                          {item.variant_details} × {item.quantity}
                        </Text>
                      </div>
                      <Text strong>€{parseFloat(item.subtotal).toFixed(2)}</Text>
                    </div>
                  ))}
                </Space>

                <Divider />

                <div className="order-summary">
                  <div className="summary-row">
                    <Text type="secondary">{t('common.subtotal')}:</Text>
                    <Text>€{parseFloat(ord.subtotal || 0).toFixed(2)}</Text>
                  </div>
                  <div className="summary-row">
                    <Text type="secondary">{t('checkout.shipping')}:</Text>
                    <Text>€{parseFloat(ord.shipping_cost || 0).toFixed(2)}</Text>
                  </div>
                  <div className="summary-row">
                    <Text strong>{t('common.total')}:</Text>
                    <Text strong style={{ color: '#FF9A76' }}>
                      €{parseFloat(ord.total_amount || 0).toFixed(2)}
                    </Text>
                  </div>
                </div>
              </Card>
            ))}

            {/* Combined Total */}
            <Card className="order-details-card">
              <div className="order-summary">
                <div className="summary-row">
                  <Title level={4} style={{ margin: 0 }}>{t('orderConfirmation.grandTotal')}:</Title>
                  <Title level={4} style={{ margin: 0, color: '#FF9A76' }}>
                    €{unifiedTotal.toFixed(2)}
                  </Title>
                </div>
              </div>
            </Card>
          </>
        ) : (
          /* SINGLE ORDER */
          <Card className="order-details-card">
            <Title level={4}>{t('orderConfirmation.orderDetails')}</Title>
            <Divider />

            <Descriptions column={{ xs: 1, sm: 2 }} bordered>
              <Descriptions.Item label={t('orderConfirmation.orderNumber')}>
                <Text strong>{order.order_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('common.status')}>
                <Tag color={getStatusColor(order.status)}>
                  {t(`orders.status.${order.status.toLowerCase()}`) || order.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('orderConfirmation.partner')}>
                {order.partner_name}
              </Descriptions.Item>
              <Descriptions.Item label={t('orderConfirmation.orderDate')}>
                {new Date(order.created_at).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('orderConfirmation.shippingAddress')} span={2}>
                {order.shipping_address}
              </Descriptions.Item>
              {order.notes && (
                <Descriptions.Item label={t('common.notes')} span={2}>
                  {order.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Title level={5}>{t('orderConfirmation.itemsOrdered')}</Title>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {order.items.map((item) => (
                <Card key={item.id} type="inner" size="small">
                  <div className="order-item">
                    <div>
                      <Text strong>{item.product_name}</Text>
                      <br />
                      <Text type="secondary">
                        {item.variant_details} × {item.quantity}
                      </Text>
                    </div>
                    <Text strong>€{parseFloat(item.subtotal).toFixed(2)}</Text>
                  </div>
                </Card>
              ))}
            </Space>

            <Divider />

            <div className="order-summary">
              <div className="summary-row">
                <Text type="secondary">{t('common.subtotal')}:</Text>
                <Text>€{parseFloat(order.subtotal).toFixed(2)}</Text>
              </div>
              <div className="summary-row">
                <Text type="secondary">{t('checkout.shipping')}:</Text>
                <Text>€{parseFloat(order.shipping_cost).toFixed(2)}</Text>
              </div>
              <Divider />
              <div className="summary-row">
                <Title level={4} style={{ margin: 0 }}>{t('common.total')}:</Title>
                <Title level={4} style={{ margin: 0, color: '#FF9A76' }}>
                  €{parseFloat(order.total_amount).toFixed(2)}
                </Title>
              </div>
            </div>
          </Card>
        )}

        <div className="action-buttons">
          <Space size="large">
            <Button
              size="large"
              icon={<FileTextOutlined />}
              onClick={() => navigate('/orders')}
            >
              {t('orderConfirmation.viewAllOrders')}
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<ShoppingOutlined />}
              onClick={() => navigate('/products')}
            >
              {t('orderConfirmation.continueShopping')}
            </Button>
          </Space>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
